import React, { useCallback, useRef, useState, useMemo, useEffect } from "react"
import { Marker, Popup, NavigationControl, FullscreenControl, ScaleControl, GeolocateControl, Map, Source, Layer } from 'react-map-gl'
import "maplibre-gl/dist/maplibre-gl.css"
import "mapbox-gl/dist/mapbox-gl.css"
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css'
import { M_Button } from "../M_Button"
import { M_MessageBoard } from "../M_MessageBoard"
import { M_InputTextField } from "../M_InputTextField"
import Pin from "./pin"
import MapboxDraw from "@mapbox/mapbox-gl-draw"
import { Tooltip } from "@mui/material"
import { AdjustOutlined, PanoramaFishEyeOutlined, Clear, Done, SignpostOutlined, SatelliteAltOutlined, Route, DirectionsOff, ControlCamera, LocationOn, LocationOff } from "@mui/icons-material"
import './M_Map.css'
import * as turf from '@turf/turf'
import { useTranslation } from "react-i18next"
import { DynamicPoint } from "./dynamicPoint"
import PropTypes from "prop-types"

const MAPBOX_TOKEN = 'pk.eyJ1Ijoiam9hb21hcmFjbyIsImEiOiJjbTRrMzFyYzQwODBtMm1zMWg1emp2cDE4In0.ZKCrjBBIPyf9k1DU-uyqSQ'

export const MMap = ({
    circlesNumber, circlesData, setCirclesData, polygonCreation, setPolygonsData,
    dynamicGeoPoints, satelliteView, findMyLoc, fullscreen, controlScale,
    navigationControl, initalMapState, mapPoints, realTimePoint, height, pointGet, numberGetPoints, points, setPoints, polygonsData,
    onDrawCreate, onDrawUpdate, onDrawDelete, onZoomChange, initialMapView, onMapViewChange, maxPolygons, exclusiveCircleOrPolygon }) => {

    const initialViewState = {
        longitude: initalMapState?.longitude || -8.09,
        latitude: initalMapState?.latitude || 39.65,
        zoom: initalMapState?.zoom || 6,
        pitch: 0,
        bearing: 0
    }

    const { t } = useTranslation()
    const [mapView, setMapView] = useState(initialMapView || "streets-v11")
    const [showRoute, setShowRoute] = useState(true)
    const [isSelectingPoint, setIsSelectingPoint] = useState(false)
    const [showPins, setShowPins] = useState(true)
    const mapRef = useRef()
    const drawRef = useRef()
    const lastViewRef = useRef({ longitude: null, latitude: null })

    //circles
    const [circleMode, setCircleMode] = useState(false)
    const [dialogCircle, setDialogCircle] = useState(false)
    const [circleCount, setCircleCount] = useState(0)

    const [popupInfo, setPopupInfo] = useState(null)

    useEffect(() => {
        if (initialMapView) {
            setMapView(initialMapView)
        }
    }, [initialMapView])

    useEffect(() => {
        if (!mapRef.current || !initalMapState?.latitude || !initalMapState?.longitude) return

        const nextView = {
            longitude: Number(initalMapState.longitude || -8.09),
            latitude: Number(initalMapState.latitude || 39.65),
            zoom: Number(initalMapState.zoom || 6),
        }

        const hasChanged =
            lastViewRef.current.longitude !== nextView.longitude ||
            lastViewRef.current.latitude !== nextView.latitude

        if (!hasChanged) return

        lastViewRef.current = {
            longitude: nextView.longitude,
            latitude: nextView.latitude,
        }

        const map = mapRef.current.getMap()
        map.flyTo({
            center: [nextView.longitude, nextView.latitude],
            zoom: nextView.zoom,
            essential: true
        })
    }, [initalMapState])


    const onSelectCity = useCallback(({ longitude, latitude }) => {
        const map = mapRef.current?.getMap()
        if (map) {
            const bbox = turf.bbox(turf.circle([longitude, latitude], 1, { units: "kilometers" }))
            map.fitBounds(bbox, { padding: 50, duration: 2000 })
        }
    }, [])

    const pins = useMemo(
        () => {
            if (!points && !mapPoints) return null
            const allPoints = [...points, ...(mapPoints || [])]
            return allPoints?.map((point, key) => {
                const hasExtraData = point.image || point.description || point.city || point.state
                return (
                    <Marker
                        data-testid="marker"
                        key={`marker-${key}`}
                        longitude={point.longitude}
                        latitude={point.latitude}
                        anchor="bottom"
                        {...(hasExtraData && {
                            onClick: (e) => {
                                e.originalEvent.stopPropagation()
                                setPopupInfo(point)
                            },
                        })}
                    >
                        <Pin size={"20px"} />
                    </Marker>
                )
            })
        },
        [points, mapPoints]
    )

    const polygonFeatureCollection = useMemo(() => {
        if (!polygonsData) return null

        if (polygonsData.type === "FeatureCollection" && Array.isArray(polygonsData.features)) {
            return polygonsData
        }

        if (Array.isArray(polygonsData)) {
            const features = polygonsData.filter(
                (feature) => feature?.type === "Feature" && feature?.geometry?.type === "Polygon"
            )
            return { type: "FeatureCollection", features }
        }

        if (typeof polygonsData === "object") {
            const features = Object.values(polygonsData).filter(
                (feature) => feature?.type === "Feature" && feature?.geometry?.type === "Polygon"
            )
            return { type: "FeatureCollection", features }
        }

        return null
    }, [polygonsData])


    const onUpdate = useCallback(e => {
        if (!setPolygonsData) return
        setPolygonsData(prev => ({
            ...prev,
            ...Object.fromEntries(e.features.map(feature => [feature.id, feature])),
        }))
    }, [setPolygonsData])

    const decrementCircleCount = () => {
        setCircleCount((prevCount) => prevCount - 1)
    }

    const onDelete = useCallback((e) => {
        if (!setPolygonsData) return
        setPolygonsData((prev) => {
            const updated = { ...prev }
            e.features.forEach((feature) => {
                delete updated[feature.id]
                if (feature.geometry.type === "Polygon" && feature.properties?.isCircle) {
                    decrementCircleCount()
                }
            })
            return updated
        })
    }, [setPolygonsData])

    const handleMapLoad = () => {
        const map = mapRef.current.getMap()
        const draw = new MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: polygonCreation, trash: circlesNumber || polygonCreation },
            styles: [
                {
                    id: 'gl-draw-line',
                    type: 'line',
                    filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
                    layout: {
                        'line-cap': 'round',
                        'line-join': 'round',
                    },
                    paint: {
                        'line-color': 'red',
                        'line-width': 2,
                    },
                },
                {
                    id: 'gl-draw-polygon-fill',
                    type: 'fill',
                    filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
                    paint: {
                        'fill-color': 'green',
                        'fill-opacity': 0.5,
                    },
                },
                {
                    id: 'gl-draw-polygon-stroke-active',
                    type: 'line',
                    filter: ['all', ['==', '$type', 'Polygon'], ['==', 'active', 'true']],
                    layout: {
                        'line-cap': 'round',
                        'line-join': 'round',
                    },
                    paint: {
                        'line-color': 'red',
                        'line-width': 2,
                    },
                },
                {
                    id: 'gl-draw-point',
                    type: 'circle',
                    filter: ['all', ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
                    paint: {
                        'circle-radius': 5,
                        'circle-color': 'red',
                    },
                },
            ],
        })
        drawRef.current = draw
        map.addControl(draw)

        map.on('draw.create', (e) => {
            const createdFeature = e?.features?.[0]
            const createdIsCircle = !!createdFeature?.properties?.isCircle

            if (exclusiveCircleOrPolygon && createdFeature?.geometry?.type === "Polygon") {
                const toDelete = draw.getAll().features
                    .filter((feature) =>
                        feature.id !== createdFeature.id &&
                        feature.geometry?.type === "Polygon" &&
                        (!!feature.properties?.isCircle) !== createdIsCircle
                    )
                    .map((feature) => feature.id)

                if (toDelete.length > 0) {
                    draw.delete(toDelete)
                }
            }

            if (maxPolygons) {
                const polygonFeatures = draw.getAll().features.filter(
                    (feature) => feature.geometry?.type === "Polygon" && !feature.properties?.isCircle
                )

                if (polygonFeatures.length > maxPolygons) {
                    e.features.forEach((feature) => {
                        if (feature.geometry?.type === "Polygon" && !feature.properties?.isCircle) {
                            draw.delete(feature.id)
                        }
                    })
                    onDrawCreate?.({ ...e, rejected: true })
                    return
                }
            }

            onUpdate(e)
            onDrawCreate?.(e)
        })

        map.on('draw.update', (e) => {
            onUpdate(e)
            onDrawUpdate?.(e)
        })

        map.on('draw.delete', (e) => {
            onDelete(e)
            onDrawDelete?.(e)
        })
    }

    const handleClick = useCallback((e) => {
        if (!circleMode || !setCirclesData) return
        setCirclesData({ location: [e.lngLat.lng, e.lngLat.lat], radius: null })
        setDialogCircle(true)
    }, [circleMode, setCirclesData])

    const toggleCircleMode = () => {
        if (circleCount >= circlesNumber) return
        setCircleMode(!circleMode)
    }

    const drawCircle = useCallback(() => {
        if (!circlesData?.location || !circlesData?.radius || !drawRef.current) return
        const circleFeature = turf.circle(circlesData?.location, circlesData?.radius, {
            steps: 64,
            units: "kilometers",
            properties: { isCircle: true },
        })
        const addedIds = drawRef.current.add(circleFeature)
        const createdCircle = Array.isArray(addedIds) && addedIds.length > 0
            ? drawRef.current.get(addedIds[0])
            : circleFeature

        const createdEvent = { features: [createdCircle] }
        onUpdate(createdEvent)
        onDrawCreate?.(createdEvent)

        const bbox = turf.bbox(circleFeature)
        const map = mapRef.current.getMap()
        map.fitBounds(bbox, { padding: 50, duration: 2000 })
        setDialogCircle(false)
        setCircleMode(false)
        setCircleCount((prevCount) => prevCount + 1)
    }, [circlesData, onDrawCreate, onUpdate])

    const toggleMapView = () => {
        if (!mapRef.current) return

        const newStyle = mapView === 'streets-v11'
            ? 'mapbox://styles/mapbox/satellite-streets-v11'
            : 'mapbox://styles/mapbox/streets-v11'

        mapRef.current.getMap()?.setStyle(newStyle, { diff: false })
        setMapView((prevView) => {
            const nextView = prevView === 'streets-v11' ? 'satellite-streets-v11' : 'streets-v11'
            onMapViewChange?.(nextView)
            return nextView
        })
    }

    const getPoint = () => {
        const map = mapRef.current?.getMap()
        if (!map) return
        if (!isSelectingPoint) {
            setIsSelectingPoint(true)
            const handleMapClick = (e) => {
                const { lngLat } = e
                const newPoint = { latitude: lngLat.lat.toFixed(5), longitude: lngLat.lng.toFixed(5) }
                if (pointGet && typeof pointGet === 'function') {
                    pointGet(newPoint)
                }

                setPoints((prevPoints) => {
                    const newPoints = [...prevPoints]

                    if (newPoints.length < numberGetPoints) {
                        newPoints.push(newPoint)
                    } else if (newPoints.length === numberGetPoints) {
                        newPoints[numberGetPoints - 1] = newPoint
                    }
                    return newPoints
                })

                setIsSelectingPoint(false)
                map.off("click", handleMapClick)
            }

            map.on("click", handleMapClick)
        } else {
            setIsSelectingPoint(false)
        }
    }

    useEffect(() => {
        if (mapRef.current) {
            const map = mapRef.current.getMap()
            map.on("click", handleClick)
            return () => {
                map.off("click", handleClick)
            }
        }
    }, [handleClick])

    return (
        <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }} data-testid="mmap-container">
            {showPins && mapPoints?.map((city, key) => (
                <div style={{ display: 'flex', width: "calc(100vw - 2%)", border: '1px solid var(--color-grey-light)' }}>
                    <div key={city.id}>
                        <M_Button
                            id={city.key}
                            label={city.city}
                            onClick={() => onSelectCity(city)}
                        />
                    </div>
                </div>
            ))}
            <Map
                ref={process.env.NODE_ENV === "test" ? null : mapRef}
                initialViewState={initialViewState}
                style={{ width: '100%', height: height, borderRadius: '40px !important' }}
                mapStyle={`mapbox://styles/mapbox/${mapView}`}
                mapboxAccessToken={MAPBOX_TOKEN}
                onLoad={handleMapLoad}
                onZoomEnd={(e) => onZoomChange?.(e.viewState?.zoom)}
            >
                {polygonFeatureCollection?.features?.length > 0 && (
                    <Source id="mmap-polygons-source" type="geojson" data={polygonFeatureCollection}>
                        <Layer
                            id="mmap-polygons-fill"
                            type="fill"
                            paint={{
                                'fill-color': '#BD000B',
                                'fill-opacity': 0.45,
                            }}
                        />
                        <Layer
                            id="mmap-polygons-line"
                            type="line"
                            paint={{
                                'line-color': '#BD000B',
                                'line-width': 2,
                            }}
                        />
                    </Source>
                )}

                {findMyLoc && <GeolocateControl position="top-left" />}
                {fullscreen && <FullscreenControl position="top-left" />}
                {navigationControl && <NavigationControl position="top-left" data-testid="nav-control" />}
                {controlScale && <ScaleControl />}

                {circlesNumber && (
                    <Tooltip title={circleMode ? t("modeToggle") : t("modeActivate")}>
                        <button
                            data-testid="toggle-circle-mode"
                            style={{ top: '75px' }}
                            className="buttonChangeViewMapCircle"
                            onClick={() => toggleCircleMode()}
                        >
                            {circleMode ? <AdjustOutlined /> : <PanoramaFishEyeOutlined />}
                        </button>
                    </Tooltip>
                )}

                {pointGet && (
                    <Tooltip title={isSelectingPoint ? t("ClickToSelectPoint") : t("EnablePointSelection")}>
                        <button
                            data-testid="toggle-point-selection"
                            style={{ top: '150px' }}
                            className='buttonChangeViewMapCircle'
                            onClick={() => getPoint()}
                        >
                            {isSelectingPoint ? <ControlCamera style={{ fontSize: 20 }} /> : <LocationOn style={{ fontSize: 20 }} />}
                        </button>
                    </Tooltip>
                )}

                {satelliteView && (
                    <Tooltip title={mapView === "streets-v11" ? t("satelliteView") : t("defaultView")}>
                        <button
                            data-testid="toggle-map-view"
                            className='buttonChangeViewMap'
                            style={{ top: '110px' }}
                            onClick={toggleMapView}>
                            {mapView === "streets-v11" ? <SatelliteAltOutlined style={{ fontSize: 15 }} /> : <SignpostOutlined style={{ fontSize: 20 }} />}
                        </button>
                    </Tooltip>
                )}

                {mapPoints && (
                    <Tooltip title={showPins ? t("HidePins") : t("ShowPins")}>
                        <button
                            data-testid="toggle-show-pins"
                            style={{ top: '180px' }}
                            className='buttonShowPins'
                            onClick={() => setShowPins(!showPins)}>
                            {showPins ? <LocationOff style={{ fontSize: 15 }} /> : <LocationOn style={{ fontSize: 20 }} />}
                        </button>
                    </Tooltip>
                )}

                {dynamicGeoPoints && (
                    <>
                        <Tooltip title={showRoute ? t("HideRoute") : t("ShowRoute")}>
                            <button
                                data-testid="toggle-show-route"
                                style={{ top: '145px' }}
                                className='buttonShowRoute'
                                onClick={() => setShowRoute(!showRoute)}>
                                {showRoute ? <DirectionsOff style={{ fontSize: 15 }} /> : <Route style={{ fontSize: 20 }} />}
                            </button>
                        </Tooltip>

                        {showRoute && (<Tooltip title={t("FocusRota")}>
                            <button
                                data-testid="adjust-route"
                                style={{ top: '145px', right: '45px' }}
                                className="buttonAdjustRoute"
                                onClick={() => {
                                    if (dynamicGeoPoints && dynamicGeoPoints.length > 1) {
                                        const map = mapRef.current.getMap()
                                        const routeCoordinates = dynamicGeoPoints.map(point => [point.longitude, point.latitude])
                                        const bounds = turf.bbox(turf.lineString(routeCoordinates))
                                        map.fitBounds(bounds, { padding: 50, duration: 2000 })
                                    }
                                }}
                            >
                                <ControlCamera />
                            </button>
                        </Tooltip>)}

                        <DynamicPoint
                            mapRef={mapRef}
                            points={dynamicGeoPoints}
                            realTimePoint={realTimePoint}
                            setPopupInfo={setPopupInfo}
                            showRoute={showRoute}
                        />
                    </>
                )}

                {showPins && pins}

                {popupInfo && (
                    <Popup
                        data-testid="popup-info"
                        anchor="top"
                        longitude={Number(popupInfo.longitude)}
                        latitude={Number(popupInfo.latitude)}
                        onClose={() => setPopupInfo(null)}
                        closeButton={false}
                    >
                        {(popupInfo?.city && popupInfo?.state) && (
                            <div style={{ margin: '10px 5px', border: '1px solid var(--color-grey-light)', display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '210px' }}>
                                {popupInfo?.city}, {popupInfo?.state}
                            </div>
                        )}
                        {popupInfo?.image && (
                            <div style={{ margin: '10px 5px', display: 'flex', justifyContent: 'center', alignItems: 'center', minWidth: '210px' }}>
                                <img width="100%" src={popupInfo?.image} alt={popupInfo?.city} />
                            </div>
                        )}
                        {popupInfo?.description && (
                            <div style={{ margin: '10px 5px', border: '1px solid var(--color-grey-light)', paddingLeft: '5px', paddingRight: '5px', minWidth: '210px' }}>
                                {popupInfo?.description}
                            </div>
                        )}
                    </Popup>
                )}
            </Map>

            <M_MessageBoard
                open={dialogCircle}
                title={t("SetRadius")}
                body={
                    <div className='divAlignRaioCircle'>
                        <M_InputTextField
                            type={"number"}
                            required={true}
                            value={circlesData?.radius || ""}
                            onChange={(e) => setCirclesData(curr => ({ ...curr, radius: parseFloat(e.target.value) }))}
                        />
                        <span className='spaceBetweenKm'>KM</span>
                    </div>
                }
                footer={
                    <div className='divAlignFooterCircle'>
                        <M_Button
                            label={t("ButtonCancel")}
                            icon={<Clear style={{ color: 'var(--color-white)' }} />}
                            backgroundColor={'var(--color-red)'}
                            labelColor={'var(--color-white)'}
                            data-testid="cancel-circle"
                            onClick={() => {
                                setDialogCircle(false)
                                setCircleMode(false)
                            }} />
                        <M_Button
                            label={t("ButtonConfirm")}
                            icon={<Done style={{ color: 'var(--color-white)' }} />}
                            backgroundColor={'var(--color-green)'}
                            labelColor={'var(--color-white)'}
                            onClick={() => drawCircle()} />
                    </div>
                }
            />
        </div >
    )
}

MMap.propTypes = {
    circlesNumber: PropTypes.number,
    circlesData: PropTypes.object,
    setCirclesData: PropTypes.func,
    polygonCreation: PropTypes.bool,
    setPolygonsData: PropTypes.func,
    dynamicGeoPoints: PropTypes.array,
    satelliteView: PropTypes.bool,
    findMyLoc: PropTypes.bool,
    fullscreen: PropTypes.bool,
    controlScale: PropTypes.bool,
    navigationControl: PropTypes.bool,
    initalMapState: PropTypes.object,
    mapPoints: PropTypes.array,
    realTimePoint: PropTypes.array,
    polygonsData: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object
    ]),
    onDrawCreate: PropTypes.func,
    onDrawUpdate: PropTypes.func,
    onDrawDelete: PropTypes.func,
    onZoomChange: PropTypes.func,
    initialMapView: PropTypes.string,
    onMapViewChange: PropTypes.func,
    maxPolygons: PropTypes.number,
    exclusiveCircleOrPolygon: PropTypes.bool
}
